import { Fragment, lazy, Suspense, useState, type CSSProperties, type ReactNode } from 'react';
import { Badge } from '../../atoms/Badge/Badge';
import { Icon } from '../../atoms/Icon/Icon';
import { Text } from '../../atoms/Text/Text';
import { isFormatted, splitAtFigures, withoutFigureMarkers } from './answerFormat';
import styles from './StreamingText.module.css';

/**
 * Ported from spectrum-ui's `StreamingText` block — the unmodified source sits
 * in `reference/spectrum-ui/ai-assistants/` for diffing. Upstream is Tailwind +
 * lucide-react; nothing of either survives here. Every value resolves to a
 * token, the one icon comes from the Figma icon set, and the type goes through
 * `Text` wherever the colour is not a state.
 *
 * Upstream ships two variants, `Answer` and `Sources`. Only **Sources** is
 * ported, so there is no `variant` prop. `Answer` collapses the citation list
 * into three overlapping index bubbles and an "N sources" count; `Sources`
 * lists them, which is the variant that actually says where the answer came
 * from. Carrying both would mean drawing eight Figma variants to document a
 * summary nobody asked for.
 *
 * NO FIGMA NODE AT THE TIME OF WRITING — this ran code-first, like the other two
 * blocks on the AI Blocks page. See the stylesheet for the numbers that carry
 * the layout, and `parity.expected.json` once the node exists.
 */

/** One cited source. Upstream's `Citation`, minus `snippet` and `favicon`, which the Sources variant never reads. */
export type Citation = {
  id: string;
  /** The number printed in the marker, and in `[n]` inside `text`. 1-based upstream. */
  index: number;
  url: string;
  title: string;
  /**
   * What to print beside the title instead of the URL's hostname. Not in
   * upstream: DeepDive AI cites the app's own modules, whose "URLs" are hash
   * routes with no host to read — `#brand-health` would print as itself.
   */
  host?: string;
};

/** Upstream's `SuggestedPrompt`. */
export type SuggestedPrompt = {
  id: string;
  label: string;
  /** What to send if it differs from the label shown. */
  prompt?: string;
};

/** The figures behind an answer, already formatted by the host. */
export type AnswerFigures = {
  /** What the rows belong to and when (`Prime Bank · 2026-08`). */
  caption?: string;
  /** One heading per measure; the entity column needs none. */
  columns: string[];
  /** `cells` align with `columns`. A row with `href` opens its entity (a post). */
  rows: { name: string; href?: string; cells: string[] }[];
};

/** A recommended next step. `title` and `detail` read `**bold**` as the answer does. */
export type AnswerRecommendation = { title: string; detail?: string; effort?: string };

export type StreamingTextProps = {
  /**
   * The answer. Two inline forms are read out of it: `**bold**` becomes a
   * `<strong>`, and `[1]` becomes a citation marker. An answer with block
   * structure — paragraphs, lists, headings, quotes, tables — is laid out as
   * Markdown instead (`AnswerMarkdown`, 2026-10-26); one paragraph keeps the
   * inline renderer, so the Figma set's answer is drawn exactly as before.
   */
  text: string;
  /** Shows the caret and withholds the sources and follow-ups until the answer has landed. */
  streaming?: boolean;
  citations?: Citation[];
  followUps?: SuggestedPrompt[];
  onFollowUp?: (prompt: SuggestedPrompt) => void;
  /**
   * Figma `Chart` — the 224-tall slot between the sources and the follow-ups
   * (777:2009, `Chart=True`). Withheld while streaming, as the sources are: the
   * slot is hidden in both `Streaming=True` variants.
   */
  chart?: ReactNode;
  /*
   * The four sections below came with the DeepDive AI engine (2026-10-26) and
   * are NOT in Figma 777:2009: v2's answer layout, set in this block's own type
   * and spacing. Each is withheld while streaming, like the sources and chart.
   */
  /** The figures behind the answer, as a grid under the chart. */
  figures?: AnswerFigures;
  /**
   * Figures the analyst placed in `text` (Quality round, 2026-10-31): each is
   * drawn where its `[[figure:<id>]]` line stands, once the answer has landed.
   * While it streams the marker lines are hidden, so none is ever read as text.
   */
  figureNodes?: Record<string, ReactNode>;
  /** Caveats about these figures, as footnotes under a hairline. Never folded. */
  notes?: string[];
  /** Next steps, numbered, each with its effort. */
  recommendations?: AnswerRecommendation[];
  /** What the data cannot tell. Folded behind a toggle: it is true of every answer. */
  limitations?: string[];
  /**
   * Figma `State=Hover` — the `Message Actions` instance at the foot of the
   * block, drawn while streaming and once settled alike. A slot rather than the
   * molecule itself so the host owns copy, feedback and regenerate.
   */
  actions?: ReactNode;
  /**
   * Drop the 520 ceiling and fill the column. The DeepDive AI screens draw the
   * instance at 687 (the chat column's 7 tracks), which is wider than the set.
   */
  fill?: boolean;
  className?: string;
};

/** Splits on the two inline forms, keeping the delimiters as their own segments. */
const SEGMENTS = /(\*\*[^*]+\*\*|\[\d+\])/g;

/** In a chunk of its own; until it arrives the answer shows as written. */
const AnswerMarkdown = lazy(() =>
  import('./lazy/AnswerMarkdown').then((module) => ({ default: module.AnswerMarkdown })),
);

/**
 * Upstream calls `new URL(url).hostname` bare, which throws and takes the whole
 * list down on one malformed href. A source with an unparseable URL is worth
 * showing with its raw string rather than not showing the answer.
 */
function hostOf(url: string) {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

/** `**bold**` and `[n]`, the two inline forms, as elements. */
function inline(text: string): ReactNode[] {
  return text
    .split(SEGMENTS)
    .filter(Boolean)
    .map((segment, index) => {
      if (segment.startsWith('**') && segment.endsWith('**')) {
        return (
          <strong key={index} className={styles.strong}>
            {segment.slice(2, -2)}
          </strong>
        );
      }
      if (/^\[\d+\]$/.test(segment)) {
        /*
         * A `<sup>` for the semantics, but not raised: see the stylesheet.
         * The bare number is what upstream reads out too — the Sources list
         * below numbers them identically, so the marker does not need to
         * name itself.
         */
        return (
          <sup key={index} className={`${styles.marker} ${styles.markerInline}`}>
            {segment.slice(1, -1)}
          </sup>
        );
      }
      return <Fragment key={index}>{segment}</Fragment>;
    });
}

/** How many rows still get a longer entry delay before they all arrive together. */
const MAX_STAGGER_STEPS = 4;

/** One run of the answer's text, as Markdown or on the inline renderer. */
function answerText(text: string, streaming: boolean) {
  return isFormatted(text) ? (
    <Text
      as="div"
      size="sm"
      leading="normal"
      color="secondary"
      className={`${styles.answer} ${styles.markdown}`}
    >
      <Suspense fallback={<p className={styles.mdPending}>{text}</p>}>
        <AnswerMarkdown text={text} streaming={streaming} />
      </Suspense>
      {streaming && <span className={styles.caret} aria-hidden />}
    </Text>
  ) : (
    <Text as="p" size="sm" leading="normal" color="secondary" className={styles.answer}>
      {inline(text)}
      {streaming && <span className={styles.caret} aria-hidden />}
    </Text>
  );
}

/**
 * The figures behind an answer as a grid: one row per entity, one column per
 * measure. Drawn under the answer for an old saved chat, and wherever the
 * analyst placed a table (Quality round, 2026-10-31).
 */
export function FiguresTable({ figures, title }: { figures: AnswerFigures; title?: string }) {
  return (
    <figure className={styles.figures}>
      <figcaption className={styles.sectionHead}>
        <Text as="span" size="xs" weight="medium" color="tertiary">
          {title ?? 'Figures'}
        </Text>
        {figures.caption && (
          <Text as="span" size="xs" color="tertiary">
            {figures.caption}
          </Text>
        )}
      </figcaption>
      <div className={styles.mdTableWrap}>
        <table className={styles.mdTable}>
          <thead>
            <tr>
              <th className={styles.mdCell} aria-label="Entity" />
              {figures.columns.map((column) => (
                <th key={column} className={`${styles.mdCell} ${styles.figureValue}`}>
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {figures.rows.map((row) => (
              <tr key={row.name}>
                <th scope="row" className={`${styles.mdCell} ${styles.figureName}`}>
                  {row.href ? (
                    <a className={styles.mdLink} href={row.href} target="_blank" rel="noreferrer">
                      {row.name}
                    </a>
                  ) : (
                    row.name
                  )}
                </th>
                {row.cells.map((cell, index) => (
                  <td
                    key={figures.columns[index] ?? index}
                    className={`${styles.mdCell} ${styles.mdNumber} ${styles.figureValue}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

export function StreamingText({
  text,
  streaming = false,
  citations = [],
  followUps = [],
  onFollowUp,
  chart,
  figures,
  figureNodes,
  notes = [],
  recommendations = [],
  limitations = [],
  actions,
  fill = false,
  className,
}: StreamingTextProps) {
  const [limitationsOpen, setLimitationsOpen] = useState(false);
  const settled = !streaming;
  const hasChart = chart !== undefined && chart !== null && chart !== false;
  const hasActions = actions !== undefined && actions !== null && actions !== false;

  return (
    /*
     * `aria-busy` replaces upstream's `aria-hidden` on the paragraph. Upstream
     * hides the whole answer from assistive tech while it streams and never
     * announces it afterwards, so a screen reader user gets the answer only if
     * they happen to re-read the region. Marking the region busy says the same
     * thing without withholding the text. Not in Figma.
     */
    <div
      className={[styles.block, fill ? styles.fill : null, className].filter(Boolean).join(' ')}
      aria-busy={streaming || undefined}
    >
      {(settled
        ? splitAtFigures(text)
        : [{ kind: 'text' as const, text: withoutFigureMarkers(text) }]
      ).map((part, index, parts) =>
        part.kind === 'text' ? (
          <Fragment key={`text-${index}`}>
            {answerText(part.text, streaming && index === parts.length - 1)}
          </Fragment>
        ) : figureNodes?.[part.id] ? (
          <div key={`figure-${part.id}`} className={styles.chart}>
            {figureNodes[part.id]}
          </div>
        ) : null,
      )}

      {settled && citations.length > 0 && (
        <ul className={styles.sources}>
          {citations.map((citation) => (
            <li key={citation.id} className={styles.source}>
              <span className={styles.marker}>{citation.index}</span>
              <span className={styles.sourceBody}>
                {/*
                 * Not in upstream, and the one substantive addition here: the
                 * Sources variant renders titles it holds a URL for as plain
                 * text, so the sources of an answer cannot be opened. A list
                 * of sources whose items are inert is the defect this variant
                 * exists to avoid. Resting appearance is unchanged — no link
                 * colour, no underline until hover — so this reads as the same
                 * design with the link it was missing.
                 */}
                <a
                  className={styles.sourceLink}
                  href={citation.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {citation.title}
                </a>
                <Text size="xs" weight="medium" color="tertiary" className={styles.host}>
                  {citation.host ?? hostOf(citation.url)}
                </Text>
              </span>
            </li>
          ))}
        </ul>
      )}

      {settled && hasChart && <div className={styles.chart}>{chart}</div>}

      {settled && figures && figures.rows.length > 0 && <FiguresTable figures={figures} />}

      {settled && notes.length > 0 && (
        <div className={styles.notes}>
          {notes.map((note, index) => (
            <Text key={index} as="p" size="sm" leading="normal" color="tertiary">
              {inline(note)}
            </Text>
          ))}
        </div>
      )}

      {settled && recommendations.length > 0 && (
        <div className={styles.section}>
          <Text as="p" size="xs" weight="medium" color="tertiary" className={styles.followUpsLabel}>
            What to do
          </Text>
          <ol className={styles.recommendations}>
            {recommendations.map((item, index) => (
              <li key={index} className={styles.recommendation}>
                <span className={styles.marker}>{index + 1}</span>
                <span className={styles.recommendationBody}>
                  <span className={styles.recommendationTitle}>
                    {/* One flex item for the whole title, so the row's gap never splits it. */}
                    <span>{inline(item.title)}</span>
                    {item.effort && <Badge type="gray">{item.effort}</Badge>}
                  </span>
                  {item.detail && (
                    <Text as="span" size="sm" leading="normal" color="tertiary">
                      {inline(item.detail)}
                    </Text>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {settled && limitations.length > 0 && (
        <div className={styles.section}>
          <button
            type="button"
            className={styles.limitationsToggle}
            aria-expanded={limitationsOpen}
            onClick={() => setLimitationsOpen((open) => !open)}
          >
            <Icon name="info" className={styles.chevron} />
            <span className={styles.followUpLabel}>
              {limitationsOpen ? 'Hide' : 'What'} this data can’t tell you ({limitations.length})
            </span>
          </button>
          {limitationsOpen && (
            <ul className={styles.limitationsList}>
              {limitations.map((item, index) => (
                <Text key={index} as="li" size="sm" leading="normal" color="tertiary">
                  {inline(item)}
                </Text>
              ))}
            </ul>
          )}
        </div>
      )}

      {settled && followUps.length > 0 && (
        <div className={styles.followUps}>
          <Text as="p" size="xs" weight="medium" color="tertiary" className={styles.followUpsLabel}>
            Follow-ups
          </Text>
          <ul className={styles.followUpList}>
            {followUps.map((suggestion, index) => (
              <li
                key={suggestion.id}
                className={styles.followUpItem}
                /* A row index, not a design value — the delay itself is a token. */
                style={{ '--st-stagger': Math.min(index, MAX_STAGGER_STEPS) } as CSSProperties}
              >
                <button
                  type="button"
                  className={styles.followUp}
                  onClick={() => onFollowUp?.(suggestion)}
                >
                  {/*
                   * Upstream's `CornerDownLeft` — the return key — has no
                   * equivalent in the Figma icon set. `chevron right` is what
                   * this system already uses for forward movement (pagination,
                   * next month, breadcrumb separator), which is what asking a
                   * follow-up is, and it takes upstream's nudge-right on hover
                   * more naturally than a return glyph does. Cheaper and more
                   * consistent than adding a 51st icon for one row.
                   */}
                  <Icon name="chevronRight" className={styles.chevron} />
                  {/*
                   * A plain span rather than `Text`, following Button's
                   * `.label`: the colour here belongs to the button's hover
                   * state, and `Text` would bring its own colour class to
                   * argue with at equal specificity.
                   */}
                  <span className={styles.followUpLabel}>{suggestion.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasActions && <div className={styles.actions}>{actions}</div>}
    </div>
  );
}
